import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import apiClientAdmin from "../../../../services/apiClientAdmin";
import { getHeaders as getAssetHeaders } from "../../../ApiHeaders/apiHeaders";
import { getHeaders } from "../../../ApiHeaders/apiHeadersEntitlements";
import { SplitDivider, useResizableSplit } from "../../../SplitPane/ResizableSplit";
import {
  EXTERNAL_TENANT_USERS_QUERY,
  isAmpEntitlementDomain,
} from "../../constants/entitlementDomains";
import {
  clearUserCache,
  getCollectionObjectIds,
  getRoleEffectiveAccess,
  getUserEffectiveAccess,
  RoleEffectiveAccess,
  UserEffectiveAccess,
} from "../../api/entitlementsAdminApi";
import { getAssetLookup, resetAssetLookup } from "../../api/assetAdminApi";
import { cacheClearedMessage } from "../../api/entitlementsMessages";
import { AccessTabId, AccessTabs, MemberRow } from "./AccessSections";
import type { GroupAsset } from "./AssetGroupsTable";
import AccessExplorerList, { ExplorerMode, RoleRow, UserRow } from "./AccessExplorerList";

const base = () => process.env.REACT_APP_ENTITLEMENTS_API || "";

async function fetchList(path: string): Promise<any[]> {
  const res = await fetch(`${base()}${path}`, { headers: await getHeaders() });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const json = await res.json();
  return json?.response?.data ?? [];
}

/** Company names for the user list; a failure only blanks the Company column. */
async function fetchCompanyNames(): Promise<Record<number, string>> {
  try {
    const res = await apiClientAdmin.get("/company/", { headers: await getAssetHeaders() });
    const list: any[] = res?.data?.response?.data ?? [];
    return Object.fromEntries(list.map((c) => [c.company_id, c.company_name]));
  } catch {
    return {};
  }
}

const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Request failed");

const fullName = (p: { first_name?: string | null; last_name?: string | null }) =>
  [p.first_name, p.last_name].filter(Boolean).join(" ");

// The gray action button the admin forms use (UserForm's Refresh access).
const BUTTON =
  "flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300";

const FIRST_TAB: Record<ExplorerMode, AccessTabId> = { role: "members", user: "roles" };

const AccessExplorer: React.FC<{ canDelete?: boolean }> = ({ canDelete }) => {
  const [mode, setMode] = useState<ExplorerMode>("role");
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [baseUsers, setBaseUsers] = useState<(UserRow & { companyId?: number })[]>([]);
  const [companies, setCompanies] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<number | null>(null);
  const [roleAccess, setRoleAccess] = useState<RoleEffectiveAccess | null>(null);
  const [userAccess, setUserAccess] = useState<UserEffectiveAccess | null>(null);
  const [tab, setTab] = useState<AccessTabId>(FIRST_TAB.role);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // Bumped on every selection and mode switch; a response for an older one is dropped.
  const seq = useRef(0);
  // One load per asset group per page visit, shared by every role/user showing it.
  const groupAssets = useRef(new Map<number, Promise<GroupAsset[]>>());

  // The group's asset ids, named from the caller's asset list (read once).
  const loadGroupAssets = useCallback((collectionId: number) => {
    const cached = groupAssets.current.get(collectionId);
    if (cached) return cached;
    const p = Promise.all([getCollectionObjectIds(collectionId), getAssetLookup()]).then(
      ([ids, assets]) => {
        const byId = new Map(assets.map((a: any) => [a.asset_id, a]));
        return ids
          .map((id): GroupAsset => {
            const a: any = byId.get(id);
            return {
              asset_id: id,
              asset_name: a?.asset_name ?? null,
              generation_subtype_code: a?.generation_subtype_code ?? null,
              iso_code: a?.iso_code ?? null,
            };
          })
          .sort((x, y) => (x.asset_name ?? "~").localeCompare(y.asset_name ?? "~") || x.asset_id - y.asset_id);
      },
    );
    groupAssets.current.set(collectionId, p);
    // A failed load is not cached, so the next expand tries again.
    p.catch(() => groupAssets.current.delete(collectionId));
    return p;
  }, []);

  useEffect(() => {
    let live = true;
    // A fresh asset list on each visit (a group's asset names).
    resetAssetLookup();
    // Company names load apart: a slow company call must not hold up the lists.
    fetchCompanyNames().then((c) => live && setCompanies(c));
    Promise.all([fetchList("/role"), fetchList(`/user/?${EXTERNAL_TENANT_USERS_QUERY}`)])
      .then(([r, u]) => {
        if (!live) return;
        setRoles(
          r
            .filter((x) => isAmpEntitlementDomain(x.domain))
            .map((x) => ({ id: x.role_id, role_name: x.role_name, domain: x.domain })),
        );
        setBaseUsers(
          u.map((x) => ({
            id: x.user_id,
            entra: x.entra_object_id,
            name: fullName(x) || x.email || x.login_name || `#${x.user_id}`,
            email: x.email || "",
            company: "",
            companyId: x.company_id,
          })),
        );
      })
      .catch((e) => live && setError(errMsg(e)));
    return () => {
      live = false;
    };
  }, []);

  // Company names arrive after the user list; merge them in when they do.
  const users: UserRow[] = useMemo(
    () => baseUsers.map((u) => ({ ...u, company: (u.companyId != null && companies[u.companyId]) || "" })),
    [baseUsers, companies],
  );

  const switchMode = (m: ExplorerMode) => {
    seq.current += 1;
    setMode(m);
    setTab(FIRST_TAB[m]);
    setSelected(null);
    setRoleAccess(null);
    setUserAccess(null);
    setError("");
    setNotice("");
  };

  const onSelect = async (id: number) => {
    const mine = ++seq.current;
    setSelected(id);
    setError("");
    setNotice("");
    setRoleAccess(null);
    setUserAccess(null);
    try {
      if (mode === "role") {
        const r = await getRoleEffectiveAccess(id);
        if (mine === seq.current) setRoleAccess(r);
      } else {
        const u = await getUserEffectiveAccess(id);
        if (mine === seq.current) setUserAccess(u);
      }
    } catch (e) {
      if (mine === seq.current) setError(errMsg(e));
    }
  };

  const refresh = async () => {
    const entra = users.find((u) => u.id === selected)?.entra;
    if (!entra) {
      setError("This user has no Entra object id, so the cache cannot be cleared.");
      return;
    }
    try {
      const r = await clearUserCache(entra);
      setNotice(cacheClearedMessage(r.amp + r.mobile));
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const roleView = mode === "role" ? roleAccess : null;
  const userView = mode === "user" ? userAccess : null;
  const access = roleView ?? userView;
  const disabled = userView !== null && userView.enabled !== "Y";
  const noGrants =
    !!access &&
    access.ui_pages.length + access.api_permissions.length + access.asset_groups.length === 0;

  // Company comes from the user list already loaded for the left panel.
  const memberRows: MemberRow[] | undefined = useMemo(
    () =>
      roleView?.members.map((m) => ({
        user_id: m.user_id,
        name: fullName(m) || m.email || `#${m.user_id}`,
        email: m.email || "",
        company: users.find((u) => u.id === m.user_id)?.company || "",
        enabled: m.enabled ?? null,
      })),
    [roleView, users],
  );

  const selectedRole = useMemo(
    () => (mode === "role" ? roles.find((r) => r.id === selected) : undefined),
    [mode, roles, selected],
  );
  const selectedUser = useMemo(
    () => (mode === "user" ? users.find((u) => u.id === selected) : undefined),
    [mode, users, selected],
  );

  const title =
    selected === null
      ? `Select a ${mode}`
      : mode === "role"
        ? roleView?.role_name ?? selectedRole?.role_name ?? ""
        : selectedUser?.name ?? userView?.email ?? "";
  const subtitle =
    selected === null
      ? "Pick a role or a user on the left."
      : mode === "role"
        ? `Domain: ${roleView?.domain ?? selectedRole?.domain ?? ""}`
        : selectedUser?.email || userView?.email || "";

  const split = useResizableSplit("xl");
  return (
    <div
      ref={split.containerRef}
      style={split.containerStyle}
      className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}
    >
      {/* Left: one child (toggle, search and list), per the admin-tab-panel height rule. */}
      <div
        className={`admin-tab-panel flex-grow flex flex-col xl:grow-0 xl:basis-[36%] xl:self-stretch bg-white relative ${split.leftClassName}`}
      >
        <AccessExplorerList
          mode={mode}
          onModeChange={switchMode}
          roles={roles}
          users={users}
          selectedId={selected}
          onSelect={onSelect}
        />
      </div>

      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch min-w-0 bg-white rounded-xl shadow-md">
        <div className="bg-white p-6 rounded-lg shadow-md font-poppins flex flex-col flex-1 min-h-0">
          <div className="flex justify-between items-start gap-4 mb-4">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-700 truncate">{title}</h2>
              {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>
            {mode === "user" && canDelete && userAccess && (
              <button type="button" onClick={refresh} className={`${BUTTON} shrink-0`}>
                <FaSyncAlt className="w-4 h-4" />
                Refresh access
              </button>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 mb-4" role="alert">
              {error}
            </p>
          )}
          {notice && <p className="text-sm text-green-700 mb-4">{notice}</p>}

          {disabled && (
            <p className="text-sm bg-amber-100 border border-amber-300 text-amber-700 rounded-lg p-3 mb-4">
              This user is disabled; they have no access.
            </p>
          )}

          {selected !== null && !access && !error && (
            <p className="text-sm text-gray-500">Loading access...</p>
          )}
          {access && !disabled && noGrants && (
            <p className="bg-[#f9f9f9] p-3 mb-4 border rounded-lg shadow-inner text-sm text-gray-500 italic">
              {`This ${mode} has no pages, APIs or asset groups.`}
            </p>
          )}
          {access && (
            <AccessTabs
              access={access}
              showRole={mode === "user"}
              members={roleView ? memberRows : undefined}
              roles={userView ? userView.roles : undefined}
              disabled={disabled}
              loadGroupAssets={loadGroupAssets}
              tab={tab}
              onTabChange={setTab}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessExplorer;
